import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Configure,
  useCurrentRefinements,
  usePagination,
  useSortBy,
  useStats
} from 'react-instantsearch';
import { InstantSearchNext } from 'react-instantsearch-nextjs';

import {
  ProductFilter,
  ProductFilterModal,
  ProductParentCategories
} from '@/components/atomic/atoms';
import { Accordion, ProductCard } from '@/components/atomic/molecules';
import { BottomModal, FilterModal } from '@/components/atomic/nuclei';
import {
  CategoryMobileHeaderOrganism,
  ProductListOrganism
} from '@/components/atomic/organism';
import { staticMediaStoreBaseURL } from '@/config/common';
import searchAPIConfig from '@/config/searchAPI';
import getSerializedRichText from '@/helpers/editor';
import createClient from '@/lib/instantSearchClient';
import { getCategoryListingPageH1Title } from '@/services/categories.service';
import { HOST_PORTAL_FQDN } from '@/services/connections.service';
import { getAuthToken } from '@/services/identity.service';

const INDEX_NAME = 'products_weightage_desc';

const SORT_ITEMS = [
  { value: 'products_weightage_desc', label: 'Relevance' },
  { value: 'products_price_asc', label: 'Price: Low to High' },
  { value: 'products_price_desc', label: 'Price: High to Low' }
];

const CategoryMobileContent = ({
  category,
  description,
  faq,
  isFilter,
  setIsFilter,
  isSorting,
  setIsSorting,
  openTab,
  setOpenTab,
  priceRange,
  productListingRef,
  handleBackButtonClick,
  updateCurrentUrl
}) => {
  const { nbHits } = useStats();
  const { currentRefinement: currentSort, refine: refineSort } = useSortBy({
    items: SORT_ITEMS
  });
  const { items: activeRefinements } = useCurrentRefinements();
  const {
    currentRefinement: currentPage,
    isFirstPage,
    isLastPage,
    refine: refinePage
  } = usePagination();

  const renderProductsCount = () => (
    <div className='flex items-center my-2.5 px-5'>
      <h1 className='inline-block truncate pr-1'>
        <span className='font-medium text-sm font-Montserrat text-nero leading-5'>
          {getCategoryListingPageH1Title(category)}
        </span>
      </h1>
      <span className='flex-shrink-0 font-normal text-sm font-Montserrat text-dim-gray leading-5'>
        {' - '}
        {nbHits}
        {' products'}
      </span>
    </div>
  );

  const renderPagination = () => {
    if (isFirstPage && isLastPage) return null;
    return (
      <div className='px-5 py-5 font-Montserrat flex items-center justify-center'>
        {!isFirstPage && (
          <div
            onClick={() => {
              refinePage(currentPage - 1);
              window.scrollTo(0, 0);
            }}
            className='text-cinnabar border border-gray w-1/2 text-sm leading-17px rounded-md flex justify-center items-center cursor-pointer h-9'
            data-qa='option'
            data-key='Previous'
          >
            <Image
              src={`${staticMediaStoreBaseURL}/icons/prev-arrow.svg`}
              alt='Previous'
              className='me-2'
              height={12}
              width={12}
            />
            <span className='ms-2'> Previous </span>
          </div>
        )}
        {!isLastPage && (
          <div
            onClick={() => {
              refinePage(currentPage + 1);
              window.scrollTo(0, 0);
            }}
            className='text-cinnabar border border-gray ms-5 w-1/2 text-sm leading-17px rounded-md flex justify-center items-center cursor-pointer h-9'
            data-qa='option'
            data-key='Next'
          >
            <span className='me-2'> Next </span>
            <Image
              src={`${staticMediaStoreBaseURL}/icons/next-arrow.svg`}
              alt='Next'
              className='ms-2'
              height={12}
              width={12}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        id='product-list'
        ref={productListingRef}
        className='scroll-mt-23'
      >
        {renderProductsCount()}
        <ProductListOrganism />
        {renderPagination()}
        <div className='flex flex-col gap-4 mb-16 px-5'>
          {getSerializedRichText(description) && (
            <Accordion
              {...{
                heading: `About ${category.name}`,
                richText: description
              }}
            />
          )}
          {getSerializedRichText(faq) && (
            <Accordion
              {...{ heading: 'Frequently Asked Questions', richText: faq }}
            />
          )}
        </div>
      </div>
      <div className='fixed w-full bottom-0 z-10'>
        <ProductFilter
          sortOrderShowHideHandler={() => setIsSorting(true)}
          filterByShowHideHandler={() => setIsFilter(true)}
          filterCount={activeRefinements.length}
        />
      </div>
      <div className={`${!isSorting ? 'hidden' : ''}`}>
        <BottomModal
          paramData={{
            modalHeight: 'min-h-43.5',
            label: 'Sort By',
            setCloseModal: setIsSorting,
            showHeader: true,
            handler: () => setIsSorting(false),
            hideClose: true
          }}
        >
          <div
            className='flex flex-col px-5 text-matterhorn'
            onClick={() => setIsSorting(false)}
          >
            {SORT_ITEMS.map((item) => (
              <div
                key={item.value}
                className={`py-3 cursor-pointer ${
                  currentSort === item.value
                    ? 'text-cinnabar font-semibold'
                    : ''
                }`}
                onClick={() => refineSort(item.value)}
              >
                {item.label}
              </div>
            ))}
          </div>
        </BottomModal>
      </div>
      <div className={`${!isFilter ? 'hidden' : ''}`}>
        <FilterModal
          paramData={{
            modalHeight: 'min-h-full',
            setCloseModal: setIsFilter,
            showHeader: true,
            handler: () => setIsFilter(false),
            handleBackButtonClick
          }}
        >
          <ProductFilterModal
            setCloseModal={setIsFilter}
            updateCurrentUrl={updateCurrentUrl}
            openTab={openTab}
            setOpenTab={setOpenTab}
            priceRange={priceRange}
          />
        </FilterModal>
      </div>
    </>
  );
};

const CategoryMobileLayoutTemplate = ({
  categories,
  category,
  categorySeoDetails,
  categorySlug,
  initialProducts,
  parentCategories,
  parentCategoryName,
  parentSlug,
  priceRange,
  productsCount,
  showProductSubCategory,
  subCategories,
  subCategorySlug
}) => {
  const router = useRouter();
  const asPath = usePathname();

  const [showModal, setShowModal] = useState(false);
  const [isSorting, setIsSorting] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [CloseAnimation, setCloseAnimation] = useState(true);
  const [currentUrl, setCurrentUrl] = useState();
  const [openTab, setOpenTab] = useState(1);

  const productListingRef = useRef(null);

  const { description, faq } = categorySeoDetails;

  useEffect(() => {
    productListingRef.current.scrollIntoView();
  }, []);

  const searchClient = useMemo(
    () =>
      createClient({
        headers: () => ({
          'x-access-token': getAuthToken(),
          'x-origin-domain': HOST_PORTAL_FQDN
        }),
        url: searchAPIConfig.productListAPIEndPoint
      }),
    []
  );

  const categoryFilter = useMemo(() => {
    const slug = subCategorySlug || categorySlug || parentSlug;
    return `assignedToCategories:${slug}`;
  }, [subCategorySlug, categorySlug, parentSlug]);

  useEffect(() => {
    document.body.style.overflow = 'unset';
    setShowModal(false);
  }, [parentSlug]);

  const upIcon = '/icons/up-arrow-gray.svg';

  const handleModal = () => {
    const isModalVisible = showModal;
    if (!isModalVisible) {
      document.body.style.overflow = 'hidden';
    }
    if (isModalVisible) {
      document.body.style.overflow = 'unset';
    }

    setCloseAnimation(!isModalVisible);
    setTimeout(() => {
      setShowModal(!isModalVisible);
    }, 200);
  };

  const updateCurrentUrl = () => setCurrentUrl(window.location.href);
  const handleBackButtonClick = () => {
    if (currentUrl) router.push(currentUrl);
  };

  const [isServerSide, setIsServerSide] = useState(true);
  useEffect(() => {
    setIsServerSide(false);
  }, []);

  return (
    <div
      className='md:hidden'
      key={asPath}
    >
      <CategoryMobileHeaderOrganism
        {...{
          categories,
          categorySlug,
          handleModal,
          heading: parentCategoryName,
          parentSlug,
          routeTo: '/',
          showProductSubCategory,
          subCategories,
          subCategorySlug,
          showOnScroll: true
        }}
      />

      {showModal ? (
        <div
          onClick={() => setShowModal(false)}
          className={`fixed z-120 w-full h-full inset-0 top-12 bg-nero bg-opacity-80 flex items-start animated ${
            CloseAnimation ? 'fade-in' : 'fade-out'
          }`}
        >
          <div
            className={`z-50 bg-white w-full relative px-5 transition-all ${
              CloseAnimation ? 'slide-down' : 'slide-up'
            }`}
          >
            <ProductParentCategories categories={parentCategories} />
            <div
              onClick={(e) => {
                handleModal(e);
              }}
              className='border border-gray bg-white rounded-full w-5 h-5 box-border flex justify-center absolute -bottom-2 right-4'
            >
              <Image
                src={`${staticMediaStoreBaseURL}${upIcon}`}
                alt='Up Arrow'
                className='w-full mx-auto max-w-2.5'
                height={10}
                width={10}
              />
            </div>
          </div>
        </div>
      ) : (
        ''
      )}

      {!isServerSide ? (
        <InstantSearchNext
          indexName={INDEX_NAME}
          searchClient={searchClient}
          routing={true}
        >
          <Configure
            filters={categoryFilter}
            hitsPerPage={50}
          />
          <CategoryMobileContent
            {...{
              category,
              description,
              faq,
              isFilter,
              setIsFilter,
              isSorting,
              setIsSorting,
              openTab,
              setOpenTab,
              priceRange,
              productListingRef,
              productsCount,
              handleBackButtonClick,
              updateCurrentUrl
            }}
          />
        </InstantSearchNext>
      ) : (
        <>
          <div
            id='product-list'
            ref={productListingRef}
            className='scroll-mt-23'
          >
            <div className='flex items-center my-2.5 px-5'>
              <h1 className='inline-block truncate pr-1'>
                <span className='font-medium text-sm font-Montserrat text-nero leading-5'>
                  {getCategoryListingPageH1Title(category)}
                </span>
              </h1>
              <span className='flex-shrink-0 font-normal text-sm font-Montserrat text-dim-gray leading-5'>
                {' - '}
                {productsCount}
                {' products'}
              </span>
            </div>
            <div
              className='grid grid-cols-1 xs:auto-rows-fr xs:grid-cols-2 lg:grid-cols-3 justify-between gap-y-5 md:gap-y-6
        xs:gap-x-4 md:gap-x-6 px-5 md:px-0 pb-16 md:pb-0 mt-1 md:my-5px mx-0 md:mx-5px'
            >
              {initialProducts?.length &&
                initialProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                  />
                ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CategoryMobileLayoutTemplate;
